import React, { useCallback, useEffect, useRef, useState } from 'react'

import Modal from '../../components/modal'
import iconNotifications from '../../design/icons/notifications.svg'

import NotificationDetail from './NotificationDetail'
import NotificationsModal from './NotificationsModal'
import {
  fetchRecentNotifications,
  fetchUnreadCount,
  formatNotificationDate,
  markNotificationRead,
} from './notificationsApi'

const RECENT_LIMIT = 6
const POLL_INTERVAL_MS = 60000

const NotificationsBell = ({ Alert }) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [listModalOpen, setListModalOpen] = useState(false)
  const [detailNotification, setDetailNotification] = useState(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const [recent, setRecent] = useState([])
  const [loadingRecent, setLoadingRecent] = useState(false)
  const menuRef = useRef(null)

  const refreshUnreadCount = useCallback(() => {
    fetchUnreadCount()
      .then((data) => {
        if (data.status === 'ok') {
          setUnreadCount(data.body?.count ?? 0)
        }
      })
      .catch(() => {})
  }, [])

  const loadRecent = useCallback(() => {
    setLoadingRecent(true)
    fetchRecentNotifications(RECENT_LIMIT)
      .then((data) => {
        setLoadingRecent(false)
        if (data.status === 'ok') {
          setRecent(Array.isArray(data.body) ? data.body : [])
        }
      })
      .catch(() => {
        setLoadingRecent(false)
      })
  }, [])

  useEffect(() => {
    refreshUnreadCount()
    const timer = setInterval(refreshUnreadCount, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [refreshUnreadCount])

  useEffect(() => {
    if (!menuOpen) return

    loadRecent()

    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [loadRecent, menuOpen])

  const toggleMenu = () => {
    setMenuOpen((open) => !open)
  }

  const handleNotificationClick = async (item) => {
    setMenuOpen(false)

    let notification = item
    if (!item?.readAt) {
      const data = await markNotificationRead(item.notificationId)
      if (data.status === 'ok') {
        notification = data.body || item
        refreshUnreadCount()
        loadRecent()
      }
    }

    setDetailNotification(notification)
  }

  const handleOpenAll = () => {
    setMenuOpen(false)
    setListModalOpen(true)
  }

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount)

  return (
    <>
      <div className="header-notifications-menu" ref={menuRef}>
        <button
          type="button"
          className="header-icon-btn header-notifications-btn"
          aria-label="Notificaciones"
          aria-expanded={menuOpen}
          aria-haspopup="true"
          onClick={toggleMenu}
        >
          <img className="header-toolbar-icon" src={iconNotifications} alt="" />
          {unreadCount > 0 && (
            <span className="header-notifications-badge" aria-label={`${unreadCount} sin leer`}>
              {badgeLabel}
            </span>
          )}
        </button>

        {menuOpen && (
          <div className="header-notifications-dropdown" role="menu">
            <div className="header-notifications-dropdown-title">Notificaciones</div>

            {loadingRecent && (
              <div className="header-notifications-empty">Cargando…</div>
            )}

            {!loadingRecent && recent.length === 0 && (
              <div className="header-notifications-empty">No hay notificaciones</div>
            )}

            {!loadingRecent &&
              recent.map((item) => (
                <button
                  key={item.notificationId}
                  type="button"
                  className={`header-notification-item${item.readAt ? '' : ' header-notification-item--unread'}`}
                  role="menuitem"
                  onClick={() => handleNotificationClick(item)}
                >
                  <span className="header-notification-item-title">{item.title}</span>
                  <span className="header-notification-item-preview">{item.preview || item.body}</span>
                  <span className="header-notification-item-date">
                    {formatNotificationDate(item.createdAt)}
                  </span>
                </button>
              ))}

            <button
              type="button"
              className="header-notifications-more"
              onClick={handleOpenAll}
            >
              Ver más…
            </button>
          </div>
        )}
      </div>

      {detailNotification && (
        <Modal
          title={detailNotification.title || 'Notificación'}
          modalId="notification-dropdown-detail-modal"
          modalClose={() => setDetailNotification(null)}
          Alert={Alert}
          width="640px"
        >
          <NotificationDetail notification={detailNotification} />
        </Modal>
      )}

      {listModalOpen && (
        <NotificationsModal
          onClose={() => setListModalOpen(false)}
          onRead={refreshUnreadCount}
          Alert={Alert}
        />
      )}
    </>
  )
}

export default NotificationsBell
